<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  findPreset,
  LENGTH_UNITS,
  type LengthUnit,
  RESOLUTION_PRESETS,
  type ScreenSpec,
} from '../model/ppi'

const props = defineProps<{ modelValue: ScreenSpec }>()
const emit = defineEmits<{ 'update:modelValue': [ScreenSpec] }>()

const { t } = useI18n()

// Drafts are strings so a half-typed or cleared field stays exactly as typed; the
// spec is re-emitted on every keystroke and the panel simply hides while it is
// unusable. No Apply button: the whole point of this tool is immediate feedback.
const horizontal = ref(String(props.modelValue.horizontalPixels))
const vertical = ref(String(props.modelValue.verticalPixels))
const diagonal = ref(String(props.modelValue.diagonal))
const unit = ref<LengthUnit>(props.modelValue.unit)

watch(
  () => props.modelValue,
  (spec) => {
    horizontal.value = String(spec.horizontalPixels)
    vertical.value = String(spec.verticalPixels)
    diagonal.value = String(spec.diagonal)
    unit.value = spec.unit
  },
)

const pixelsInvalid = (value: string): boolean => {
  const parsed = Number(value)
  return !Number.isInteger(parsed) || parsed <= 0
}

const horizontalInvalid = computed(() => pixelsInvalid(horizontal.value))
const verticalInvalid = computed(() => pixelsInvalid(vertical.value))
const diagonalInvalid = computed(() => {
  const parsed = Number(diagonal.value)
  return !Number.isFinite(parsed) || parsed <= 0
})
const anyInvalid = computed(
  () => horizontalInvalid.value || verticalInvalid.value || diagonalInvalid.value,
)
const errorId = computed(() => (anyInvalid.value ? 'screen-error' : undefined))

// Shows the matching preset's name when the typed resolution is one of them, and
// falls back to "Custom" for anything else — including a preset turned on its side.
const preset = computed(() => findPreset(Number(horizontal.value), Number(vertical.value)))

function applyPreset(event: Event): void {
  // Bound to a const so the narrowing survives into the callback below.
  const target = event.target
  if (!(target instanceof HTMLSelectElement)) return
  const chosen = RESOLUTION_PRESETS.find((option) => option.name === target.value)
  if (!chosen) return
  horizontal.value = String(chosen.width)
  vertical.value = String(chosen.height)
  publish()
}

function publish(): void {
  emit('update:modelValue', {
    horizontalPixels: Number(horizontal.value),
    verticalPixels: Number(vertical.value),
    diagonal: Number(diagonal.value),
    unit: unit.value,
  })
}
</script>

<template>
  <form class="screen-form" @submit.prevent>
    <div class="screen-form__field">
      <label for="resolution-preset">{{ t('input.preset') }}</label>
      <select
        id="resolution-preset"
        class="screen-form__preset"
        :value="preset?.name ?? ''"
        @change="applyPreset"
      >
        <option value="">{{ t('input.presetCustom') }}</option>
        <!-- Pixel counts are written the way every spec sheet writes them, without
             a thousands separator, so they are not run through the number formatter. -->
        <option v-for="option in RESOLUTION_PRESETS" :key="option.name" :value="option.name">
          {{ option.name }} — {{ option.width }} × {{ option.height }}
        </option>
      </select>
    </div>

    <div class="screen-form__field">
      <label for="horizontal-pixels">{{ t('input.horizontalPixels') }}</label>
      <div class="screen-form__control">
        <input
          id="horizontal-pixels"
          v-model="horizontal"
          class="mono"
          type="number"
          min="1"
          step="1"
          inputmode="numeric"
          autocomplete="off"
          :aria-invalid="horizontalInvalid"
          :aria-describedby="errorId"
          @input="publish()"
        />
        <span class="screen-form__suffix">{{ t('input.px') }}</span>
      </div>
    </div>

    <div class="screen-form__field">
      <label for="vertical-pixels">{{ t('input.verticalPixels') }}</label>
      <div class="screen-form__control">
        <input
          id="vertical-pixels"
          v-model="vertical"
          class="mono"
          type="number"
          min="1"
          step="1"
          inputmode="numeric"
          autocomplete="off"
          :aria-invalid="verticalInvalid"
          :aria-describedby="errorId"
          @input="publish()"
        />
        <span class="screen-form__suffix">{{ t('input.px') }}</span>
      </div>
    </div>

    <div class="screen-form__field">
      <label for="diagonal">{{ t('input.diagonal') }}</label>
      <div class="screen-form__control">
        <input
          id="diagonal"
          v-model="diagonal"
          class="mono"
          type="number"
          min="0"
          step="0.1"
          inputmode="decimal"
          autocomplete="off"
          :aria-invalid="diagonalInvalid"
          :aria-describedby="errorId"
          @input="publish()"
        />
        <label class="visually-hidden" for="diagonal-unit">{{ t('input.unit') }}</label>
        <select id="diagonal-unit" v-model="unit" @change="publish()">
          <option v-for="option in LENGTH_UNITS" :key="option" :value="option">
            {{ t(`unit.${option}`) }}
          </option>
        </select>
      </div>
    </div>

    <p v-if="anyInvalid" id="screen-error" class="screen-form__error" role="alert">
      {{ t('input.invalid') }}
    </p>
  </form>
</template>

<style scoped>
.screen-form {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-4);
  align-items: flex-end;
}

.screen-form__field {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

label {
  font-size: var(--text-sm);
  color: var(--color-text-muted);
}

.screen-form__control {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

input {
  width: 7.5rem;
  padding: var(--space-2) var(--space-3);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  color: var(--color-text);
  font-size: var(--text-base);
}

input[aria-invalid='true'] {
  border-color: var(--color-danger);
}

select {
  max-width: 100%;
  padding: var(--space-2) var(--space-3);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  color: var(--color-text);
  font: inherit;
  font-size: var(--text-sm);
  cursor: pointer;
}

.screen-form__suffix {
  font-size: var(--text-sm);
  color: var(--color-text-muted);
}

/* Takes the full row so it never squeezes the fields when it appears. */
.screen-form__error {
  flex-basis: 100%;
  margin: 0;
  font-size: var(--text-sm);
  color: var(--color-danger);
}
</style>
